// client/src/components/campaign/ContentApprovalPanel.jsx
//
// Approval controls + review history for a single social post (On-Screen 2.0).
// Drop into SchedulePost, PostDetail, ScheduledPostsList, the Content Calendar.
//
//   <ContentApprovalPanel tenantId={t} post={{ id, approval_status }} onChanged={fn} />

import React, { useCallback, useEffect, useState } from 'react';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';
import {
  submitForReview, approvePost, requestChanges, rejectPost, getReviewHistory,
  APPROVAL_LABELS,
} from '../../services/contentApprovalApi';

// Inline hex pairs [background, text] rather than Tailwind bg-/text- utility
// classes — see the identical note in CampaignList.jsx: the desktop shell
// forces an inherited white text color inside .tenant-content at >=1024px,
// which silently wins over these utility classes and renders the pill
// white-on-white. An inline style is a direct declaration on the element
// itself, so it can't be shadowed by an inherited ancestor color.
const SURFACE = {
  idea: ['#f1f5f9', '#334155'],
  draft: ['#f1f5f9', '#334155'],
  in_review: ['#fef3c7', '#92400e'],
  changes_requested: ['#ffedd5', '#9a3412'],
  approved: ['#d1fae5', '#065f46'],
  rejected: ['#fee2e2', '#991b1b'],
  archived: ['#e5e7eb', '#6b7280'],
};

export default function ContentApprovalPanel({ tenantId, post, onChanged, compact = false }) {
  const status = post?.approval_status || post?.approvalStatus || 'draft';
  const postId = post?.id;
  const { role } = getSessionLocal();
  const isApprover = can(role, 'posting', 'schedule') || can(role, 'posting', 'update');
  const canSubmit = can(role, 'posting', 'submit') || isApprover;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!postId) return;
    try {
      const res = await getReviewHistory(postId);
      setHistory(res.reviews || []);
    } catch {
      setHistory([]);
    }
  }, [postId]);

  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory, loadHistory]);

  const run = async (fn, needsNote) => {
    if (needsNote && !note.trim()) { setError('Add a note explaining what needs to change'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await fn(postId, note.trim() || undefined);
      setNote('');
      if (showHistory) loadHistory();
      onChanged?.(res.approvalStatus || status);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  // Inline-styled pill for the compact/embedded variant — Tailwind bg/text
  // utilities get stomped by host themes (e.g. the Bootstrap scheduled-posts
  // list forces `span { color: … !important }`), so use hard hexes there.
  const PILL_HEX = {
    idea: ['#334155', '#e2e8f0'], draft: ['#334155', '#e2e8f0'],
    in_review: ['#92400e', '#fde68a'], changes_requested: ['#9a3412', '#fed7aa'],
    approved: ['#065f46', '#a7f3d0'], rejected: ['#991b1b', '#fecaca'],
    archived: ['#3f3f46', '#d4d4d8'],
  };
  const [pbg, pfg] = PILL_HEX[status] || PILL_HEX.draft;
  const pill = compact ? (
    <span className="os-cap-pill" style={{ background: pbg, color: pfg, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
      {APPROVAL_LABELS[status] || status}
    </span>
  ) : (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: (SURFACE[status] || SURFACE.draft)[0],
        color: (SURFACE[status] || SURFACE.draft)[1],
      }}
    >
      {APPROVAL_LABELS[status] || status}
    </span>
  );

  if (!postId) return pill;

  return (
    <div
      className={`os-cap ${compact ? 'os-cap-compact space-y-1.5 rounded-md p-2' : 'space-y-2 rounded-lg border border-black/10 p-3 dark:border-white/10'}`}
      style={compact ? { background: '#1e2330', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.08)' } : undefined}
    >
      {/* Keep the panel readable when embedded in themed surfaces. */}
      {compact && (
        <style>{`.os-cap-compact, .os-cap-compact div, .os-cap-compact button, .os-cap-compact input { color: #e5e7eb !important; }
          .os-cap-compact a { color: #93c5fd !important; }
          .os-cap-compact .os-cap-pill { color: ${pfg} !important; }
          .os-cap-compact input { background: #12151c !important; border-color: #374151 !important; }`}</style>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {!compact && <span className="text-xs font-semibold uppercase tracking-wide opacity-60">Approval</span>}
          {pill}
        </div>
        <button type="button" onClick={() => setShowHistory((s) => !s)} className="text-xs underline opacity-60">
          {showHistory ? 'Hide history' : 'History'}
        </button>
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      {(['in_review'].includes(status) && isApprover) || (['draft', 'idea', 'changes_requested'].includes(status) && canSubmit) ? (
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note"
          className="w-full rounded border border-black/15 bg-transparent px-2 py-1 text-xs dark:border-white/15"
        />
      ) : null}

      <div className="flex flex-wrap gap-2">
        {['draft', 'idea', 'changes_requested', 'rejected'].includes(status) && canSubmit && (
          <button type="button" disabled={busy} onClick={() => run(submitForReview)}
            className="rounded bg-cobalt px-2.5 py-1 text-xs text-white disabled:opacity-50">
            Submit for review
          </button>
        )}
        {status === 'in_review' && isApprover && (
          <>
            <button type="button" disabled={busy} onClick={() => run(approvePost)}
              className="rounded bg-emerald-600 px-2.5 py-1 text-xs text-white disabled:opacity-50">
              Approve
            </button>
            <button type="button" disabled={busy} onClick={() => run(requestChanges, true)}
              className="rounded bg-amber-500 px-2.5 py-1 text-xs text-white disabled:opacity-50">
              Request changes
            </button>
            <button type="button" disabled={busy} onClick={() => run(rejectPost)}
              className="rounded border border-red-400 px-2.5 py-1 text-xs text-red-600 disabled:opacity-50">
              Reject
            </button>
          </>
        )}
        {status === 'in_review' && !isApprover && (
          <span className="text-xs opacity-60">Waiting on a manager to review.</span>
        )}
      </div>

      {showHistory && (
        <ul className="space-y-1 border-t border-black/10 pt-2 text-xs dark:border-white/10">
          {history == null && <li className="opacity-60">Loading…</li>}
          {history && history.length === 0 && <li className="opacity-60">No review activity yet.</li>}
          {(history || []).map((r) => (
            <li key={r.id} className="flex justify-between gap-2">
              <span>
                <span className="font-medium">{r.actor_username || 'Someone'}</span>{' '}
                {r.action.replace(/_/g, ' ')}
                {r.note ? ` — ${r.note}` : ''}
              </span>
              <span className="whitespace-nowrap opacity-50">{new Date(r.createdon).toLocaleDateString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
